import React, { useCallback, useMemo } from 'react';
import { Checkbox, Flex, Text, Tooltip } from '@televet/kibble-ui';
import { MergeDisabledReason } from '../utils';
import { useTracking } from 'shared/providers/TrackingProvider';
import { MergeProfileEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IMergeProfilesClientSearchResultProps {
  clientName: string;
  clientId: string;
  disabledReason: MergeDisabledReason | undefined;
  petNames: string[];
  selectedMergeProfile: string | undefined;
  setSelectedMergeProfile: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const MergeProfilesClientSearchResult = ({
  clientName,
  clientId,
  disabledReason,
  petNames,
  selectedMergeProfile,
  setSelectedMergeProfile,
}: IMergeProfilesClientSearchResultProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const isSelected = useMemo(() => selectedMergeProfile === clientId, [clientId, selectedMergeProfile]);

  const handleProfileSelected = useCallback((): void => {
    if (!!disabledReason) {
      const { code } = disabledReason;
      if (code === 3) {
        trackEvent(MergeProfileEvents.MergeLoyaltyPointsAttempt);
      }
      if (code === 5) {
        trackEvent(MergeProfileEvents.MergeCareMembershipAttempt);
      }
      return;
    }

    if (!isSelected) {
      setSelectedMergeProfile(clientId);
    } else {
      setSelectedMergeProfile(undefined);
    }
  }, [disabledReason, isSelected, trackEvent, setSelectedMergeProfile, clientId]);

  return (
    <Tooltip label={disabledReason?.reason ?? ''} placement="left" titleCaseLabel={false}>
      <Flex
        p={3}
        background="background.primaryAlpha.hover"
        opacity={!!disabledReason ? 0.4 : 1}
        borderRadius={4}
        cursor={!!disabledReason ? 'not-allowed' : 'pointer'}
        onClick={handleProfileSelected}
      >
        <Flex alignSelf="center" pointerEvents="none" pr={2}>
          <Checkbox size="lg" isChecked={isSelected} variant="round" />
        </Flex>
        <Flex direction="column">
          <Text pb={1}>{clientName}</Text>
          <Text size="sm">{`Pet${petNames.length > 1 ? 's' : ''}: ${
            petNames.length ? petNames.join(', ') : 'None'
          }`}</Text>
        </Flex>
      </Flex>
    </Tooltip>
  );
};

export default MergeProfilesClientSearchResult;
